<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useDisplay, useTheme } from 'vuetify/framework'
import { ERouteNames } from '@/router/ERouteNames'

import { useApiStore } from '@/modules/apiStore/stores/apiStore'
import { useAuditRegisterStore } from '@/modules/audits/stores/useAuditRegisterStore/useAuditRegisterStore'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { formatDateOnly, formatShortName } from '@/utils/formatters'


import UniversalFilterTree from '@/components/createdUi/UniversalFilterTree.vue'
import CollapsibleCell from '@/components/createdUi/CollapsibleCell.vue'
import CollapsibleText from '@/components/createdUi/CollapsibleText.vue'
import ShortFio from '@/components/createdUi/ShortFio.vue'

import { type IFilterConfig, useTableFilters } from '@/composables/useTableFilters'
import { useTableItemsPerPage } from '@/composables/useTableItemsPerPage'
import { useOnboardingContext } from '@/modules/onboarding/composables/useOnboardingContext'
import { useOnboardingDemoRows } from '@/modules/onboarding/composables/useOnboardingDemoRows'
import { useOnboardingStore } from '@/modules/onboarding/stores/onboardingStore'
import { AUDITS_ANCHORS, AUDITS_CONTEXTS } from '@/modules/onboarding/scenarios/audits/anchors'
import { AUDITS_CARD_DEMO_INDEX } from '@/modules/onboarding/scenarios/audits/adapter'
import { ONBOARDING_DEMO_QUERY } from '@/modules/onboarding/utils/demoRoute'

const theme = useTheme()
const { xs, smAndDown } = useDisplay()
const router = useRouter()
const route = useRoute()

const feedbackStore = useFeedbackStore()
const auditRegister = useAuditRegisterStore()
const apiStore = useApiStore()

const isRoot = computed(() => route.name === ERouteNames.AUDITS)

// Refs для двусторонней синхронизации пагинации и сортировки
const { currentSortString, nextCursor } = storeToRefs(auditRegister)
const { itemsPerPage } = useTableItemsPerPage('auditRegister', 25)

// Синхронизация с хранилищем реестра аудитов
auditRegister.itemsPerPage = itemsPerPage.value
watch(itemsPerPage, newVal => {
  auditRegister.itemsPerPage = newVal
})

const datePresets = [
  {
    id: 'today',
    label: 'Сегодня',
    getRange: () => {
      const d = new Date(); d.setHours(0, 0, 0, 0)
      return { start: formatDateForInput(d) }
    },
  },
  {
    id: 'yesterday',
    label: 'Вчера',
    getRange: () => {
      const d = new Date(); d.setDate(d.getDate() - 1); d.setHours(0, 0, 0, 0)
      return { start: formatDateForInput(d) }
    },
  },
  {
    id: 'current_week',
    label: 'Текущая неделя',
    getRange: () => {
      const today = new Date(); today.setHours(0, 0, 0, 0)
      const day = today.getDay(); const diff = (day === 0 ? -6 : 1) - day
      const start = new Date(today); start.setDate(today.getDate() + diff)
      return { start: formatDateForInput(start) }
    },
  },
  {
    id: 'current_month',
    label: 'Текущий месяц',
    getRange: () => {
      const today = new Date()
      const start = new Date(today.getFullYear(), today.getMonth(), 1)
      return { start: formatDateForInput(start) }
    },
  },
]

function getStatusLabel(status: string) {
  if (!status) return 'Ожидает'
  const statuses = apiStore.responsibilityEntriesStatuses
  return (statuses as Record<string, string>)[status] || status
}

const filterConfigs: IFilterConfig[] = [
  { key: 'numbers', labelPrefix: 'Номер: ', type: 'number' },
  { key: 'standards', labelPrefix: 'Норматив: ', type: 'tree' },
  { key: 'parents', labelPrefix: 'Предмет проверки: ', type: 'tree' },
  { key: 'departments', labelPrefix: 'Подразделение: ', type: 'tree' },
  { key: 'managers', labelPrefix: 'Руководитель: ', type: 'tree' },
  { key: 'auditors', labelPrefix: 'Аудитор: ', type: 'tree' },
  { key: 'planned_start', labelPrefix: 'Дата нач.: ', type: 'date_single', dateFormat: 'YYYY-MM-DD' },
  { key: 'planned_end', labelPrefix: 'Дата кон.: ', type: 'date_single', dateFormat: 'YYYY-MM-DD' },
  { key: 'audit_type', labelPrefix: 'Вид: ', type: 'tree' },
  { key: 'status', labelPrefix: 'Статус: ', type: 'status', getCustomLabel: getStatusLabel },
  { key: 'is_overdue', labelPrefix: 'Просрочено: ', type: 'boolean', getCustomLabel: () => 'Просрочено' },
  { key: 'requires_action', labelPrefix: 'Требует действия: ', type: 'boolean', getCustomLabel: () => 'Требует действия' },
]

const tableLogic = useTableFilters({
  filters: computed(() => auditRegister.filters),
  searchChoices: computed(() => auditRegister.searchChoices) as any,
  filterChoices: computed(() => auditRegister.filterChoices) as any,
  filterLoading: auditRegister.filterLoading,
  fetchFilterChoices: (key: string) => auditRegister.fetchFilterChoices(key),
  fetchData: (cursor?: string, limit?: number, throwOnError?: boolean) => auditRegister.fetchAudits(cursor, limit, throwOnError),
  realtimeEnabled: true,
  currentSortString,
  itemsPerPage,
  resetFilters: () => auditRegister.resetFilters(),
  nextCursor,
  loadedItemsCount: computed(() => auditRegister.audits.length),
  totalItemsCount: computed(() => auditRegister.totalAuditsCount),
  sortMap: {
    id: 'id',
    standards: 'standards',
    parents: 'parents',
    departments: 'departments',
    audit_type: 'audit_type',
    status: 'status',
    planned_start: 'planned_start',
    planned_end: 'planned_end',
  },
  filterConfigs,
})

const isOverdueFilterActive = computed(() => auditRegister.filters.is_overdue === true)
const isRequiresActionFilterActive = computed(() => auditRegister.filters.requires_action === true)
const displayedActiveFilterChips = computed(() => tableLogic.activeFilterChips.value)

const headers = computed(() => [
  { title: '№', key: 'id', sortable: true, filterType: 'number', filterKey: 'numbers' },
  { title: 'Название', key: 'name', sortable: false },
  { title: 'Норматив', key: 'standards', sortable: true, filterType: 'tree', filterKey: 'standards' },
  { title: 'Предмет проверки', key: 'parents', sortable: true, filterType: 'tree', filterKey: 'parents' },
  { title: 'Подразделения', key: 'departments', sortable: true, filterType: 'tree', filterKey: 'departments' },
  { title: 'Руководитель', key: 'manager', sortable: false, filterType: 'tree', filterKey: 'managers' },
  { title: 'Аудиторы', key: 'auditors', sortable: false, filterType: 'tree', filterKey: 'auditors' },
  { title: 'Статус', key: 'status', sortable: true, filterType: 'tree', filterKey: 'status' },
  { title: 'Дата нач.', key: 'planned_start', sortable: true, filterType: 'date_single', filterKey: 'planned_start' },
  { title: 'Дата кон.', key: 'planned_end', sortable: true, filterType: 'date_single', filterKey: 'planned_end' },
  { title: 'Вид', key: 'audit_type', sortable: true, filterType: 'tree', filterKey: 'audit_type' },
] as any[])

const displayedAudits = computed(() => {
  const start = (tableLogic.currentPage.value - 1) * itemsPerPage.value
  const end = start + itemsPerPage.value
  return auditRegister.audits.slice(start, end)
})

const handleRowClick = async (item: any) => {
  if (item.id) {
    await router.push({
      name: ERouteNames.AUDIT_DETAIL,
      params: { id: item.id },
    })
  }
}

/* Онбординг: демо-аудиты первыми в реестре, учебная карточка открывается маршрутом карточки с меткой учебной записи */
const onboardingStore = useOnboardingStore()
const { rows: tableRows } = useOnboardingDemoRows('audit', displayedAudits)
const onboardingCardDemoId = computed(() => onboardingStore.activeDemoItems[AUDITS_CARD_DEMO_INDEX]?.list.id ?? null)
const isFiltersPanelOpen = ref<number | undefined>(undefined)

useOnboardingContext({
  names: [AUDITS_CONTEXTS.register],
  root: () => document.body,
  actions: {
    openFiltersPanel: () => {
      if (smAndDown.value)
        isFiltersPanelOpen.value = 0
    },
    openDemoCard: async () => {
      if (onboardingCardDemoId.value !== null)
        await router.push({ name: ERouteNames.AUDIT_DETAIL, params: { id: onboardingCardDemoId.value }, query: { [ONBOARDING_DEMO_QUERY]: '1' } })
    },
    closeCard: async () => { await router.push({ name: ERouteNames.AUDITS }) },
  },
})

const rowProps = (data: { item: any }) => ({
  onClick: () => handleRowClick(data.item),
  class: {
    'audit-row--overdue': data.item.is_overdue,
    'row--requires-action': data.item.requires_action,
  },
  'data-onboarding': data.item.id === onboardingCardDemoId.value ? AUDITS_ANCHORS.register.demoRow : undefined,
})

const getLabelCode = (label: string) => label ? label.split(' ')[0] : '-'
const getLabelText = (label: string) => label && label.includes(' ') ? label.substring(label.indexOf(' ') + 1) : label

// Dates helpers
const formatDateForInput = (date: Date | null): string => {
  if (!date) return ''
  const day = String(date.getDate()).padStart(2, '0')
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const year = date.getFullYear()
  return `${day}.${month}.${year}`
}

const parseDateFromInput = (dateString: string): string | null => {
  if (!dateString) return null
  const [day, month, year] = dateString.split('.').map(Number)
  if (!day || !month || !year) return null
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

const applyNumberFilterWrapper = (key: string) => {
  const inputStr = tableLogic.tempValues.value[key] || ''
  if (inputStr.trim() === '') {
    (auditRegister.filters as any)[key] = []
  } else {
    (auditRegister.filters as any)[key] = inputStr
      .split(/[,;\s]+/)
      .map((num: string) => Number.parseInt(num.trim()))
      .filter((num: number) => !isNaN(num) && num > 0)
  }
  tableLogic.filterMenus.value[key] = false
  tableLogic.triggerServerFilter()
}

const applyDateFilterWrapper = (key: string) => {
  const strDate = tableLogic.tempValues.value[`${key}_str`] || ''
  ;(auditRegister.filters as any)[key] = parseDateFromInput(strDate)
  tableLogic.triggerServerFilter()
  tableLogic.filterMenus.value[key] = false
}

const handlePresetClick = (presetObj: any, key: string) => {
  const range = presetObj.getRange()
  tableLogic.tempValues.value[`${key}_str`] = range.start
}

const applyAllFieldsFilter = () => {
  tableLogic.applySearchFilter()
}

const toggleOverdueFilter = () => {
  auditRegister.filters.is_overdue = isOverdueFilterActive.value ? null : true
  tableLogic.triggerServerFilter()
}

const toggleRequiresActionFilter = () => {
  auditRegister.filters.requires_action = isRequiresActionFilterActive.value ? null : true
  tableLogic.triggerServerFilter()
}

onMounted(async () => {
  await auditRegister.fetchAudits()

  // Автостарт только на самом реестре: при прямом заходе в карточку обучение не перебивает работу
  if (isRoot.value)
    void onboardingStore.autoStart('audits')
})
</script>

<template>
  <div v-if="isRoot" class="wrapper">
    <div>
      <!--
        На мобилке фильтры уходят в #audit-mobile-filters (toolbar).
        v-if/v-else вместо Teleport :disabled — иначе в Safari/WebKit
        иногда остаются оба набора (панель + десктопный ряд).
      -->
      <Teleport
        v-if="smAndDown"
        defer
        to="#audit-mobile-filters"
      >
        <VExpansionPanels v-model="isFiltersPanelOpen" class="w-100">
          <VExpansionPanel>
            <VExpansionPanelTitle class="d-flex align-center py-2 px-4">
              <div class="d-flex align-center ga-2">
                <VIcon icon="ri-filter-3-line" size="20" />
                <span class="text-subtitle-1 font-weight-medium">Фильтры</span>
              </div>
            </VExpansionPanelTitle>
            <VExpansionPanelText>
              <div class="d-flex flex-column ga-2 w-100">
                <VTextField
                  v-model="tableLogic.searchFilter.value"
                  class="w-100"
                  variant="outlined"
                  clearable
                  placeholder="Фильтр по ключевому слову"
                  :data-onboarding="AUDITS_ANCHORS.register.search"
                  autocomplete="off"
                  autocapitalize="off"
                  autocorrect="off"
                  spellcheck="false"
                  type="text"
                  @keyup.enter="applyAllFieldsFilter"
                />

                <VIconBtn
                  v-if="tableLogic.searchFilter.value"
                  class="w-100 rounded-sm"
                  color="primary"
                  icon="ri-filter-2-line"
                  variant="outlined"
                  :rounded="false"
                  @click="applyAllFieldsFilter"
                />

                <VBtn
                  class="w-100"
                  :prepend-icon="tableLogic.getFilterIcon('is_overdue')"
                  variant="outlined"
                  color="secondary"
                  :loading="feedbackStore.isGlobalLoading"
                  @click="toggleOverdueFilter"
                  :data-onboarding="AUDITS_ANCHORS.register.overdue"
                >
                  Просрочено
                </VBtn>

                <VBtn
                  class="w-100"
                  :prepend-icon="tableLogic.getFilterIcon('requires_action')"
                  variant="outlined"
                  color="secondary"
                  :loading="feedbackStore.isGlobalLoading"
                  @click="toggleRequiresActionFilter"
                  :data-onboarding="AUDITS_ANCHORS.register.requiresAction"
                >
                  Требует действия
                </VBtn>

                <VBtn
                  v-if="tableLogic.hasActiveFilters.value"
                  class="w-100 text-color-secondary"
                  prepend-icon="ri-delete-bin-line"
                  variant="outlined"
                  color="secondary"
                  :loading="feedbackStore.isGlobalLoading"
                  @click="tableLogic.renewDataAndResetFilters"
                  :data-onboarding="AUDITS_ANCHORS.register.resetFilters"
                >
                  Сбросить фильтры
                </VBtn>
              </div>
            </VExpansionPanelText>
          </VExpansionPanel>
        </VExpansionPanels>
      </Teleport>

      <div
        v-else
        class="d-flex align-center gap-2 w-100"
        :class="displayedActiveFilterChips.length ? 'px-4 pt-4' : 'pa-4'"
      >
          <VTextField
            v-model="tableLogic.searchFilter.value"
            class="flex-grow-0 w-25"
            variant="outlined"
            clearable
            placeholder="Фильтр по ключевому слову"
            :data-onboarding="AUDITS_ANCHORS.register.search"
            autofocus
            autocomplete="off"
            autocapitalize="off"
            autocorrect="off"
            spellcheck="false"
            type="text"
            @keyup.enter="applyAllFieldsFilter"
          />

          <VIconBtn
            v-if="tableLogic.searchFilter.value"
            color="primary"
            class="rounded-sm"
            icon="ri-filter-2-line"
            variant="outlined"
            :rounded="false"
            width="48"
            height="48"
            @click="applyAllFieldsFilter"
          />

          <VBtn
            class="ml-auto"
            :prepend-icon="tableLogic.getFilterIcon('is_overdue')"
            variant="outlined"
            color="secondary"
            height="48"
            :loading="feedbackStore.isGlobalLoading"
            @click="toggleOverdueFilter"
            :data-onboarding="AUDITS_ANCHORS.register.overdue"
          >
            Просрочено
          </VBtn>

          <VBtn
            :prepend-icon="tableLogic.getFilterIcon('requires_action')"
            variant="outlined"
            color="secondary"
            height="48"
            :loading="feedbackStore.isGlobalLoading"
            @click="toggleRequiresActionFilter"
            :data-onboarding="AUDITS_ANCHORS.register.requiresAction"
          >
            Требует действия
          </VBtn>

          <VBtn
            v-if="tableLogic.hasActiveFilters.value"
            class="text-color-secondary"
            prepend-icon="ri-delete-bin-line"
            variant="outlined"
            color="secondary"
            height="48"
            :loading="feedbackStore.isGlobalLoading"
            @click="tableLogic.renewDataAndResetFilters"
            :data-onboarding="AUDITS_ANCHORS.register.resetFilters"
          >
            Сбросить фильтры
          </VBtn>
        </div>

      <div v-if="displayedActiveFilterChips.length" class="filter-chip d-flex ml-4 align-center gap-2 py-3">
        <VChip
          v-for="chip in displayedActiveFilterChips"
          :key="chip.key || chip.id || `${chip.config?.key || chip.type}-${chip.value ?? 'no-value'}`"
          closable
          @click:close="tableLogic.removeFilter(chip)"
        >
          {{ chip.label }}
        </VChip>
      </div>

      <VDataTableServer
        :items="tableRows"
        :items-length="auditRegister.totalAuditsCount"
        :headers="headers as any"
        :items-per-page="itemsPerPage"
        :row-props="rowProps"
        class="no-row-gaps cursor-pointer-table custom-data-table custom-data-table-footer audit-register-table"
        item-value="id"
        :class="[{ 'dark-theme': theme.global.current.value.dark }]"
        @update:options="tableLogic.handlePageChange"
      >
        <template #no-data>
          <div class="text-center text-italic py-4">
            Данные отсутствуют
          </div>
        </template>

        <!-- Dynamic Headers -->
        <template v-for="col in headers.filter(h => h.filterType)" :key="col.key" #[`header.${col.key}`]="{ column }">
          <div
            class="header-content d-flex align-center"
            :data-onboarding="AUDITS_ANCHORS.register.filterPrefix + col.key"
          >
            <span>{{ column.title }}</span>

            <!-- Sort Icon -->
            <VIcon
              v-if="column.sortable"
              size="16"
              class="ml-1 custom-sort-icon"
              :icon="tableLogic.getSortIcon(col.key)"
              :class="{
                'is-active': tableLogic.isSortActive(col.key),
                'is-desc': tableLogic.isSortDesc(col.key),
              }"
            />

            <!-- Spinner -->
            <div v-if="tableLogic.filterLoading?.[col.filterKey!]" class="d-flex align-center justify-center ml-1" style="width: 28px; height: 28px">
              <VProgressCircular
                indeterminate
                color="primary"
                size="20"
                width="2"
              />
            </div>

            <VIconBtn
              v-else
              variant="plain"
              size="small"
              class="filter-icon-btn"
              :icon="tableLogic.getFilterIcon(col.filterKey!)"
              @click.stop="tableLogic.handleFilterClick(col.filterKey!)"
            />

            <!-- Number -->
            <VMenu
              v-if="col.filterType === 'number'"
              v-model="tableLogic.filterMenus.value[col.filterKey!]"
              :close-on-content-click="false"
              location="bottom"
              transition="scale-transition"
            >
              <template #activator="{ props: menuProps }">
                <div v-bind="menuProps" />
              </template>
              <VCard class="pa-4">
                <VTextField
                  v-model="tableLogic.tempValues.value[col.filterKey!]"
                  autofocus
                  placeholder="Например: 123, 456"
                  hide-details
                  width="220px"
                  @keyup.enter="applyNumberFilterWrapper(col.filterKey!)"
                />
                <div class="d-flex justify-end mt-2">
                  <VBtn
                    :class="{ 'w-100': xs }"
                    size="small"
                    variant="outlined"
                    color="secondary"
                    @click="applyNumberFilterWrapper(col.filterKey!)"
                  >
                    Применить
                  </VBtn>
                </div>
              </VCard>
            </VMenu>

            <!-- Date Single -->
            <VMenu
              v-else-if="col.filterType === 'date_single'"
              v-model="tableLogic.filterMenus.value[col.filterKey!]"
              :close-on-content-click="false"
              location="bottom"
              transition="scale-transition"
              :max-width="220"
            >
              <template #activator="{ props: menuProps }">
                <div v-bind="menuProps" />
              </template>
              <VCard class="date-filter-card">
                <VCardText class="pa-4">
                  <VTextField
                    v-model="tableLogic.tempValues.value[`${col.filterKey!}_str`]"
                    variant="outlined"
                    label="дата"
                    density="compact"
                    class="mb-3"
                    append-inner-icon="ri-calendar-line"
                    hide-details
                  />
                  <div class="d-flex flex-wrap gap-2 mb-4">
                    <VChip
                      v-for="preset in datePresets"
                      :key="preset.id"
                      class="preset-chip body-1"
                      size="small"
                      @click="handlePresetClick(preset, col.filterKey!)"
                    >
                      {{ preset.label }}
                    </VChip>
                  </div>
                  <div>
                    <VBtn
                      variant="elevated"
                      size="small"
                      block
                      :disabled="!tableLogic.tempValues.value[`${col.filterKey!}_str`]"
                      @click="applyDateFilterWrapper(col.filterKey!)"
                    >
                      Применить
                    </VBtn>
                  </div>
                </VCardText>
              </VCard>
            </VMenu>

            <!-- Tree/Status -->
            <VMenu
              v-else
              v-model="tableLogic.filterMenus.value[col.filterKey!]"
              :close-on-content-click="false"
              location="bottom start"
              transition="scale-transition"
            >
              <template #activator="{ props: menuProps }">
                <div v-bind="menuProps" />
              </template>
              <UniversalFilterTree
                :field-key="col.filterKey!"
                :store="auditRegister"
                :model-value="tableLogic.tempSelectedFilters.value[col.filterKey!] || []"
                :placeholder="`Поиск: ${column.title}`"
                @update:model-value="tableLogic.tempSelectedFilters.value[col.filterKey!] = $event"
                @apply="tableLogic.applyFilter(col.filterKey!)"
              />
            </VMenu>
          </div>
        </template>

        <!-- Cells -->
        <template #[`item.id`]="{ item }">
          <span>{{ item.id }}</span>
        </template>

        <template #[`item.name`]="{ item }">
          <div class="cell-wrap">
            {{ item.name || '-' }}
          </div>
        </template>

        <template #[`item.standards`]="{ item }">
          <CollapsibleText
            v-if="item.standards && item.standards.length"
            :text="item.standards.map(s => s.label).join(', ')"
          />
          <span v-else>-</span>
        </template>

        <template #[`item.parents`]="{ item }">
          <CollapsibleCell
            :items="item.parents"
            :collapsed-items-count="8"
            type-label="предметов проверки"
            btn-class="audit-collapsible-trigger"
          />
        </template>

        <template #[`item.manager`]="{ item }">
          <ShortFio :person="item.manager" />
        </template>

        <template #[`item.departments`]="{ item }">
          <CollapsibleText
            v-if="item.departments?.length"
            :text="item.departments.map((d: any) => d.name).filter(Boolean).join(', ')"
          />
          <span v-else>-</span>
        </template>

        <template #[`item.auditors`]="{ item }">
          <CollapsibleText
            v-if="item.auditors && item.auditors.length"
            :text="item.auditors.map(auditor => formatShortName(auditor)).filter(Boolean).join(', ')"
          />
          <span v-else>-</span>
        </template>

        <template #[`item.planned_start`]="{ item }">
          <span>{{ formatDateOnly(item.planned_start) }}</span>
        </template>

        <template #[`item.planned_end`]="{ item }">
          <span>{{ formatDateOnly(item.planned_end) }}</span>
        </template>

        <template #[`item.audit_type`]="{ item }">
          <span>{{ item.audit_type === 'planned' ? 'Плановый' : item.audit_type === 'unplanned' ? 'Внеплановый' : item.audit_type }}</span>
        </template>

        <template #[`item.status`]="{ item }">
          <VChip
            class="status-badge"
            :class="item.status"
            size="small"
          >
            {{ getStatusLabel(item.status) }}
          </VChip>
        </template>

        <template #bottom>
          <div class="custom-data-table-footer" :class="{ 'hide-next-button': tableLogic.hideNextButton.value }">
            <VDataTableFooter
              :show-current-page="true"
              :items-per-page-options="[25, 50, 100]"
              v-bind="$attrs"
              @click:prev="$emit('click:prev', $event)"
              @click:next="$emit('click:next', $event)"
              @click:first="$emit('click:first', $event)"
              @click:last="$emit('click:last', $event)"
            />
          </div>
        </template>
      </VDataTableServer>
    </div>
  </div>
  <RouterView />
</template>

<style scoped lang="scss">
:deep(.custom-data-table .v-data-table__th) {
  background-color: rgb(var(--v-theme-primary-100), var(--v-overlay-scrim-opacity)) !important;
  color: rgba(var(--v-theme-on-surface), var(--v-high-emphasis-opacity)) !important;
  font-size: 15px !important;
  font-weight: 500 !important;
  letter-spacing: 0 !important;
  line-height: 22px !important;
  position: relative;
  padding-inline: 1rem;
}

:deep(.custom-data-table .v-data-table__td) {
  color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  font-size: 15px;
  font-weight: 400;
  line-height: 20px;
  padding-inline: 1rem;
}

:deep(.custom-data-table .audit-row--overdue > .v-data-table__td) {
  background-color: rgba(var(--v-theme-error), 0.08) !important;
}

:deep(.custom-data-table tr.row--requires-action > .v-data-table__td:first-child) {
  border-inline-start: 4px solid rgb(var(--v-theme-error)) !important;
}

:deep(.audit-collapsible-trigger) {
  text-decoration: none !important;
}

:deep(.custom-data-table .v-data-table__th:not(:last-child))::after {
  content: '';
  position: absolute;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
  width: 2px;
  height: 14px;
  background-color: rgb(var(--v-theme-on-background), var(--v-custom-opacity));
}

header {
  background: rgb(var(--v-theme-background));
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

:deep(.custom-data-table td),
:deep(.custom-data-table th) {
  overflow: visible;
  white-space: normal;
  word-wrap: break-word;
}

:deep(.custom-data-table tr):hover {
  box-shadow: 0 2px 4px rgb(var(--v-shadow-key-umbra-color), var(--v-shadow-xs-opacity));
  cursor: pointer;
  background-color: var(--table-row-hover) !important;
}

:deep(.custom-data-table.dark-theme tr:hover) {
  box-shadow: 0 2px 4px rgba(99, 118, 135, 0.2) !important;
}

:deep(.v-data-table-footer) {
  display: flex;
  justify-content: center;
  align-items: center;
}

.custom-data-table-footer {
  :deep(.v-data-table-footer) {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 16px;

    .v-data-table-footer__items-per-page > span:first-child {
      display: none !important;
    }

    .v-data-table-footer__pagination {
      order: 1;
    }

    .v-data-table-footer__items-per-page {
      order: 2;
    }

    .v-data-table-footer__info {
      order: 3;
    }

    .v-pagination__last > button {
      display: none !important;
    }

    .v-pagination__next > button[aria-disabled="true"] {
      display: none !important;
    }
  }
}

.date-filter-card {
  .v-card-text {
    padding: 16px !important;
  }
  .v-chip {
    margin-bottom: 4px;
  }
}

.priority-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}

.priority-dot.low { background-color: var(--p-green-400); }
.priority-dot.medium { background-color: var(--p-orange-400); }
.priority-dot.high { background-color: var(--p-yellow-400); }
.priority-dot.critical { background-color: var(--p-red-400); }

.risk-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
  background-color: #f44336;
}

.text-italic { font-style: italic; }
.truncate {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.gap-2 { gap: 8px; }

:deep(tbody .v-data-table__tr .v-data-table__td:first-child) {
  color: rgb(var(--v-theme-primary-500)) !important;
}

:deep(.v-data-table__th) {
  white-space: nowrap;
}

:deep(.v-data-table__td) {
  padding-block: 0.75rem !important;
}

:deep(.v-data-table-header__sort-icon) {
  display: none !important;
}

.custom-sort-icon {
  font-size: 16px;
  transition: transform 0.3s ease, opacity 0.2s ease, color 0.2s ease;
  opacity: 0.15;
  display: inline-block;
  vertical-align: middle;
  cursor: pointer;

  &.is-active {
    opacity: 1 !important;
    color: rgb(var(--v-theme-primary)) !important;
  }

  &.is-desc {
    transform: rotate(180deg);
  }
}

:deep(.v-data-table__th--sortable:hover) .custom-sort-icon {
  opacity: 0.6;
}

.filter-chip {
  overflow-x: auto;
  white-space: nowrap;
}

.filter-chip .v-chip {
  flex-shrink: 0;
  max-width: none !important;
  white-space: nowrap;
  overflow: visible;
}

:deep(.custom-data-table td .cell-wrap) {
  display: flex;
  flex-wrap: wrap;
  gap: 2px 4px;
}

/* № */
:deep(.audit-register-table th:nth-child(1)),
:deep(.audit-register-table td:nth-child(1)) {
  width: 3.5rem !important;
}

/* Название */
:deep(.audit-register-table th:nth-child(2)),
:deep(.audit-register-table td:nth-child(2)) {
  width: 14rem !important;
  min-width: 14rem !important;
}

/* Норматив */
:deep(.audit-register-table th:nth-child(3)),
:deep(.audit-register-table td:nth-child(3)) {
  width: 22rem !important;
  min-width: 22rem !important;
}

/* Предмет проверки */
:deep(.audit-register-table th:nth-child(4)),
:deep(.audit-register-table td:nth-child(4)) {
  width: 12rem !important;
  min-width: 12rem !important;
}

/* Подразделения */
:deep(.audit-register-table th:nth-child(5)),
:deep(.audit-register-table td:nth-child(5)) {
  width: 17.3rem !important;
}

/* Руководитель */
:deep(.audit-register-table th:nth-child(6)),
:deep(.audit-register-table td:nth-child(6)) {
  width: 13rem !important;
}

/* Аудиторы */
:deep(.audit-register-table th:nth-child(7)),
:deep(.audit-register-table td:nth-child(7)) {
  width: 17rem !important;
}

/* Статус */
:deep(.audit-register-table th:nth-child(8)),
:deep(.audit-register-table td:nth-child(8)) {
  width: 9rem !important;
}

/* Дата нач. */
:deep(.audit-register-table th:nth-child(9)),
:deep(.audit-register-table td:nth-child(9)) {
  width: 10rem !important;
}

/* Дата кон. */
:deep(.audit-register-table th:nth-child(10)),
:deep(.audit-register-table td:nth-child(10)) {
  width: 10rem !important;
}

/* Вид */
:deep(.audit-register-table th:nth-child(11)),
:deep(.audit-register-table td:nth-child(11)) {
  width: 8rem !important;
}
</style>
